import { useState } from 'react'
import { X, Pencil, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface KeywordChipProps {
  keyword: string
  onRemove?: (() => void) | undefined
  onEdit?: ((newValue: string) => void) | undefined
  className?: string
  variant?: 'default' | 'suggested' | 'editable'
}

export function KeywordChip({
  keyword,
  onRemove,
  onEdit,
  className,
  variant = 'default',
}: KeywordChipProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(keyword)
  const isEditable = variant === 'editable'

  const handleEditClick = () => {
    setEditValue(keyword)
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    const trimmed = editValue.trim()
    if (trimmed && onEdit) {
      onEdit(trimmed)
    }
    setIsEditing(false)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditValue(keyword)
    }
  }

  if (isEditing && isEditable) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm font-medium bg-blue-50 border border-blue-200',
          className
        )}
      >
        <input
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={handleSaveEdit}
          autoFocus
          className="bg-transparent border-none outline-none text-blue-700 font-medium min-w-[60px] max-w-[200px]"
          style={{ width: `${Math.max(editValue.length, 8)}ch` }}
        />
        <button
          type="button"
          onClick={handleSaveEdit}
          className="w-4 h-4 rounded-full hover:bg-blue-200/50 flex items-center justify-center"
        >
          <Check className="w-3 h-3 text-blue-600" />
        </button>
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
        variant === 'default' && 'bg-primary/10 text-primary border border-primary/20',
        variant === 'suggested' &&
          'bg-muted text-muted-foreground border border-input hover:bg-accent hover:text-accent-foreground cursor-pointer',
        variant === 'editable' && 'bg-blue-50 text-blue-700 border border-blue-200',
        className
      )}
    >
      {keyword}
      {isEditable && onEdit ? (
        <button
          type="button"
          onClick={handleEditClick}
          className="w-4 h-4 rounded-full hover:bg-blue-200/50 flex items-center justify-center"
          title="Bewerk trefwoord"
        >
          <Pencil className="w-3 h-3 text-blue-600" />
        </button>
      ) : onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="w-4 h-4 rounded-full hover:bg-current/20 flex items-center justify-center"
        >
          <X className="w-3 h-3" />
        </button>
      ) : null}
    </span>
  )
}
